
import { City } from '@/data/cities';
import { detectCityLanguage } from './cityLanguageDetector';
import { CityLanguage } from '@/context/languages/languageData';

interface CityTranslations {
  [key: string]: Partial<Record<CityLanguage, string>>;
}

// Optimized bilingual translations: English + Local Language only
export const generateCityTranslations = (city: City): CityTranslations => {
  const cleanCityName = city.name.replace(/\s+\d+$/, '').trim();
  const localLanguage = detectCityLanguage(city);
  
  // Determine which languages to generate
  const languages: CityLanguage[] = localLanguage === 'en' 
    ? ['en'] // If already English, only generate English
    : ['en', localLanguage]; // English + local language
  
  // Helper to create bilingual translations
  const createBilingualTranslation = (enText: string, localText?: string): Partial<Record<CityLanguage, string>> => {
    if (localLanguage === 'en') {
      return { en: enText };
    }
    return {
      en: enText,
      [localLanguage]: localText || enText // Use English as fallback if local not provided
    };
  };

  // Base translations optimized for bilingual system
  const baseTranslations: CityTranslations = {
    // Main titles
    expertPsychotherapistsTitle: createBilingualTranslation(
      `Expert Psychotherapists in ${cleanCityName}`
    ),
    
    // Meta description
    metaDescription: createBilingualTranslation(
      `Find qualified psychotherapists in ${cleanCityName}, ${city.country}. Expert mental health professionals offering therapy, counseling, and psychological services.`
    ),
    
    // Call-to-action buttons
    findPsychotherapistsButton: createBilingualTranslation(
      `Find Psychotherapists in ${cleanCityName}`
    ),
    
    // Main description
    mainDescription: createBilingualTranslation(
      `Discover qualified and licensed psychotherapists in ${cleanCityName} who are dedicated to supporting your mental health journey. Our network of professional psychotherapists in ${cleanCityName} offers comprehensive psychological services.`
    ),
    
    // Why choose section
    whyChooseTitle: createBilingualTranslation(
      `Why Choose Psychotherapists in ${cleanCityName}?`
    ),
    
    // Local expertise
    localExpertise: createBilingualTranslation('Local Expertise'),
    localExpertiseDesc: createBilingualTranslation(
      `Psychotherapists in ${cleanCityName} understand the unique challenges and cultural context of your community.`
    ),
    
    // Flexible access
    flexibleAccess: createBilingualTranslation('Flexible Access'),
    flexibleAccessDesc: createBilingualTranslation(
      `Choose from in-person sessions or secure online therapy with psychotherapists in ${cleanCityName}.`
    ),
    
    // Proven results
    provenResults: createBilingualTranslation('Proven Results'),
    provenResultsDesc: createBilingualTranslation(
      `The psychotherapists in ${cleanCityName} utilize evidence-based treatment approaches with documented success rates.`
    )
  };
  
  return baseTranslations;
};

export const getLocalizedCityPath = (citySlug: string, language: CityLanguage): string => {
  if (language === 'en') {
    return `/city/${citySlug}`;
  }
  return `/${language}/city/${citySlug}`;
};
